import { Component, createContext, useCallback, useContext, useMemo, useRef, useState, useSyncExternalStore, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { useLocation, useNavigationType, type Location } from 'react-router'
import { NavigationMemory, WINDOW_SCROLL, type ViewNavigation, type ViewPosition } from './navigationMemory'
import { dismissPreviousKeyboard, keyboardViewChanged } from './keyboard'
import { createScrollAnchoringLock } from './scrollAnchoring'

const anchoringLock = createScrollAnchoringLock(() => document.documentElement.style)

interface ViewContextValue { memory: NavigationMemory; currentKey: string }
const ViewContext = createContext<ViewContextValue | null>(null)

function useViewContext(): ViewContextValue {
  const context = useContext(ViewContext)
  if (!context) throw new Error('Память экрана используется вне NavigationProvider')
  return context
}

/** Только состояние интерфейса: фильтры, выбор, черновик, позиция. Ответы API остаются в QueryClient. */
export function useViewState<T>(slot: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const { memory } = useViewContext()
  const { key } = useLocation()
  const initialRef = useRef(initial)
  initialRef.current = initial
  const read = useCallback(() => memory.read(key, slot, initialRef.current), [memory, key, slot])
  const value = useSyncExternalStore(memory.subscribe, read, read)
  const set = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    const previous = memory.read(key, slot, initialRef.current)
    memory.write(key, slot, typeof next === 'function' ? (next as (value: T) => T)(previous) : next)
  }, [memory, key, slot])
  return [value, set]
}

/** Фоновый экран под route-overlay сохраняется, но не открывает свои диалоги поверх карточки. */
export function useViewIsActive(): boolean {
  return useViewContext().currentKey === useLocation().key
}

export function useViewCanGoBack(): boolean {
  const { memory } = useViewContext()
  const { key } = useLocation()
  return useSyncExternalStore(memory.subscribe, () => memory.canGoBack(key), () => false)
}

export function useViewBackDecision(): () => 'back' | 'fallback' | 'pending' {
  const { memory } = useViewContext()
  const { key } = useLocation()
  return useCallback(() => memory.takeBack(key), [memory, key])
}

interface RouteState {
  backgroundLocation?: Location
  preserveViewScroll?: boolean
  /** Только для явной команды, которая сама позиционирует эти области. На POP не применяется. */
  resetViewScroll?: string[]
}

/** Создаётся под signed_in и с key пользователя: logout и смена пользователя уничтожают память. */
export function NavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const action = useNavigationType()
  const [memory] = useState(() => new NavigationMemory())
  const routeState = location.state as RouteState | null
  memory.prepare(location, action, routeState?.preserveViewScroll === true, routeState?.backgroundLocation?.key)
  const value = useMemo(() => ({ memory, currentKey: location.key }), [memory, location.key])
  return (
    <ViewContext value={value}>
      <ViewScroll memory={memory} location={location} action={action} windowKey={routeState?.backgroundLocation?.key ?? location.key}>
        {children}
      </ViewScroll>
    </ViewContext>
  )
}

interface ScrollProps {
  memory: NavigationMemory
  location: Location
  action: ViewNavigation
  windowKey: string
  children: ReactNode
}

/**
 * getSnapshotBeforeUpdate читает старый DOM до его замены. useEffect cleanup здесь поздно:
 * браузер уже мог обрезать scrollY до высоты нового короткого экрана.
 */
class ViewScroll extends Component<ScrollProps> {
  private restoring = false
  private frame = 0
  private timeout: ReturnType<typeof setTimeout> | undefined
  private mutations: MutationObserver | undefined
  private resize: ResizeObserver | undefined
  private previousRestoration: ScrollRestoration = 'auto'
  private settle: 'positioning' | 'painting' | 'painted' | 'unlocked' = 'positioning'
  private unlockAnchoring: (() => void) | undefined

  componentDidMount(): void {
    this.props.memory.commit(this.props.location, this.props.action)
    this.previousRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    document.addEventListener('scroll', this.onScroll, true)
    window.addEventListener('pagehide', this.capture)
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.addEventListener(event, this.interrupt, { capture: true, passive: true })
    }
    // При первом входе не трогаем якорь/прокрутку браузера. Новое посещение начинается с текущего вида.
    this.capture()
  }

  getSnapshotBeforeUpdate(previous: ScrollProps): Element | null {
    if (previous.location.key !== this.props.location.key) {
      this.captureProps(previous)
      this.holdAnchoring()
    }
    return document.activeElement
  }

  componentDidUpdate(previous: ScrollProps, _state: unknown, previousFocus: Element | null): void {
    if (previous.location.key === this.props.location.key) return
    if (keyboardViewChanged(previous.location, this.props.location)) dismissPreviousKeyboard(previousFocus)
    this.stopRestoring(true)
    const state = this.props.location.state as RouteState | null
    if (previous.location.pathname === this.props.location.pathname
      && (this.props.action === 'REPLACE' || (this.props.action === 'PUSH' && state?.preserveViewScroll))) {
      // Снимок getSnapshotBeforeUpdate точнее последнего scroll event: он снят до замены DOM.
      for (const [slot, position] of this.props.memory.positions(previous.location.key)) {
        this.props.memory.savePosition(this.props.location.key, slot, position)
      }
    }
    this.props.memory.resetPositions(this.props.location.key, this.props.action, state?.resetViewScroll ?? [])
    this.props.memory.commit(this.props.location, this.props.action)
    this.startRestoring()
  }

  componentWillUnmount(): void {
    this.stopRestoring()
    document.removeEventListener('scroll', this.onScroll, true)
    window.removeEventListener('pagehide', this.capture)
    for (const event of ['wheel', 'touchstart', 'pointerdown', 'keydown']) window.removeEventListener(event, this.interrupt, true)
    window.history.scrollRestoration = this.previousRestoration
  }

  private entryFor = (element: HTMLElement, fallback = this.props.location.key): string =>
    element.closest<HTMLElement>('[data-view-entry]')?.dataset.viewEntry ?? fallback

  private captureProps = (props: ScrollProps): void => {
    if (this.restoring) return
    const active = props.location.key
    // Под карточкой фоновая лента может перерисоваться и сдвинуть окно.
    // Её позиция уже сохранена при открытии карточки и нужна для Back.
    if (active === props.windowKey) {
      props.memory.savePosition(active, WINDOW_SCROLL, { x: window.scrollX, y: window.scrollY })
    }
    const visible = new Set<string>()
    document.querySelectorAll<HTMLElement>('[data-view-scroll]').forEach((element) => {
      const slot = element.dataset.viewScroll
      if (!slot || this.entryFor(element, active) !== active) return
      props.memory.savePosition(active, slot, { x: element.scrollLeft, y: element.scrollTop })
      visible.add(slot)
    })
    props.memory.rememberVisibleScroll(active, visible)
  }

  private capture = (): void => { this.captureProps(this.props) }

  private onScroll = (event: Event): void => {
    if (this.restoring) return
    const target = event.target
    const { memory, location: { key: active }, windowKey } = this.props
    if (target === document) {
      if (active === windowKey) memory.savePosition(active, WINDOW_SCROLL, { x: window.scrollX, y: window.scrollY })
    } else if (target instanceof HTMLElement && target.dataset.viewScroll && this.entryFor(target) === active) {
      memory.savePosition(active, target.dataset.viewScroll, { x: target.scrollLeft, y: target.scrollTop })
    }
  }

  private interrupt = (): void => {
    if (!this.restoring) return
    this.stopRestoring()
    this.capture()
  }

  private schedule = (): void => {
    if (!this.restoring || this.frame) return
    this.frame = window.requestAnimationFrame(() => {
      this.frame = 0
      this.restore()
    })
  }

  private startRestoring(): void {
    this.holdAnchoring()
    this.restoring = true
    this.mutations = new MutationObserver(this.schedule)
    this.mutations.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-view-ready'] })
    this.resize = new ResizeObserver(this.schedule)
    this.resize.observe(document.body)
    document.querySelectorAll<HTMLElement>('[data-view-scroll]').forEach((element) => this.resize?.observe(element))
    this.timeout = setTimeout(() => { this.stopRestoring(); this.capture() }, 60_000)
    this.restore()
  }

  private restore(): void {
    if (!this.restoring) return
    // Лента восстанавливает количество страниц; до окончания запроса высота ещё не окончательная.
    if (document.querySelector('[data-view-ready="false"]')) {
      this.settle = 'positioning'
      this.holdAnchoring()
      return
    }
    const { memory, windowKey } = this.props
    const desired = memory.position(windowKey, WINDOW_SCROLL) ?? { x: 0, y: 0 }
    let moved = !near({ x: window.scrollX, y: window.scrollY }, desired)
    if (moved) {
      this.holdAnchoring()
      window.scrollTo({ left: desired.x, top: desired.y, behavior: 'instant' })
    }
    let complete = near({ x: window.scrollX, y: window.scrollY }, desired)
    const found = new Map<string, Set<string>>()
    document.querySelectorAll<HTMLElement>('[data-view-scroll]').forEach((element) => {
      const slot = element.dataset.viewScroll
      if (!slot) return
      const entry = this.entryFor(element)
      if (!found.has(entry)) found.set(entry, new Set())
      found.get(entry)?.add(slot)
      const position = memory.position(entry, slot)
      if (!position) return
      if (!near({ x: element.scrollLeft, y: element.scrollTop }, position)) {
        moved = true
        this.holdAnchoring()
        element.scrollTo({ left: position.x, top: position.y, behavior: 'instant' })
        if (!near({ x: element.scrollLeft, y: element.scrollTop }, position)) complete = false
      }
    })
    if (this.props.action === 'POP') {
      // При windowY=0 возврат тоже может ждать lazy-таблицу с горизонтальной прокруткой.
      for (const entry of new Set([windowKey, this.props.location.key])) {
        for (const slot of memory.visibleScroll(entry)) {
          if (!found.get(entry)?.has(slot)) complete = false
        }
      }
    }
    if (!complete || moved) {
      this.settle = 'positioning'
      this.holdAnchoring()
    }
    if (!complete) return
    if (this.settle === 'unlocked') {
      this.stopRestoring()
      this.capture()
      return
    }
    // Синхронное чтение после scrollTo ещё не гарантирует позицию после layout.
    // Сохраняем исходный снимок до кадра после включения браузерного anchoring.
    if (this.settle === 'painted') {
      this.releaseAnchoring()
      this.settle = 'unlocked'
    } else {
      this.settle = this.settle === 'painting' ? 'painted' : 'painting'
    }
    this.schedule()
  }

  private holdAnchoring(): void {
    this.unlockAnchoring ??= anchoringLock.lock()
  }

  private releaseAnchoring(): void {
    this.unlockAnchoring?.()
    this.unlockAnchoring = undefined
  }

  private stopRestoring(keepAnchoring = false): void {
    this.restoring = false
    this.settle = 'positioning'
    if (this.frame) window.cancelAnimationFrame(this.frame)
    this.frame = 0
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = undefined
    this.mutations?.disconnect()
    this.resize?.disconnect()
    this.mutations = undefined
    this.resize = undefined
    if (!keepAnchoring) this.releaseAnchoring()
  }

  render(): ReactNode { return this.props.children }
}

function near(actual: ViewPosition, desired: ViewPosition): boolean {
  return Math.abs(actual.x - desired.x) <= 1 && Math.abs(actual.y - desired.y) <= 1
}
